import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  InputAdornment,
  Tooltip
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ColorLens as ColorLensIcon,
  FormatSize as FormatSizeIcon,
  DirectionsBoat as BoatIcon
} from '@mui/icons-material';
import MarinaAdminLayout from '../../layouts/MarinaAdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getDamageWaiverSettings, 
  updateDamageWaiverSettings,
  addVesselWaiverRate,
  updateVesselWaiverRate,
  removeVesselWaiverRate,
  DamageWaiverSettings,
  VesselWaiverRate
} from '../../firebase/services/damageWaiverService';
import { getVesselsByType } from '../../firebase/services/vesselService';
import { getRoleById } from '../../firebase/services/roleService';
import { Role } from '../../types/rbac';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const DamageWaiverPage: React.FC = () => {
  const { userProfile } = useAuth();
  const [roleName, setRoleName] = React.useState<string | null>(null);
  const [roleLoading, setRoleLoading] = React.useState<boolean>(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // State for damage waiver settings
  const [settings, setSettings] = useState<DamageWaiverSettings | null>(null);
  const [isPercentage, setIsPercentage] = useState(false);
  const [percentageValue, setPercentageValue] = useState(10);
  const [fixedAmount, setFixedAmount] = useState(25);
  const [isTaxable, setIsTaxable] = useState(false); // Whether damage waiver is subject to sales tax
  
  // UI Settings
  const [headingFontSize, setHeadingFontSize] = useState('1rem');
  const [textFontSize, setTextFontSize] = useState('0.875rem');
  const [primaryColor, setPrimaryColor] = useState('#1976d2');
  const [accentColor, setAccentColor] = useState('#4caf50');
  const [customText, setCustomText] = useState('');
  
  // Vessel specific rates
  const [vesselTypes, setVesselTypes] = useState<string[]>([]);
  const [vessels, setVessels] = useState<{ [type: string]: any[] }>({});
  const [vesselRates, setVesselRates] = useState<VesselWaiverRate[]>([]);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<VesselWaiverRate | null>(null);
  const [rateType, setRateType] = useState<'vessel' | 'type'>('type');
  const [selectedVesselType, setSelectedVesselType] = useState('');
  const [selectedVessel, setSelectedVessel] = useState('');
  const [rateIsPercentage, setRateIsPercentage] = useState(false);
  const [ratePercentage, setRatePercentage] = useState(10);
  const [rateFixed, setRateFixed] = useState(25);

  React.useEffect(() => {
    const fetchRole = async () => {
      if (userProfile && userProfile.roleId) {
        setRoleLoading(true);
        const roleDoc = await getRoleById(userProfile.roleId);
        setRoleName(roleDoc?.name || null);
        setRoleLoading(false);
      } else {
        setRoleName(null);
        setRoleLoading(false);
      }
    };
    fetchRole();
  }, [userProfile]);

  useEffect(() => {
    fetchSettings();
    fetchVessels();
  }, []);

  // Load settings from Firebase
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await getDamageWaiverSettings();
      setSettings(data);
      
      // Set form state from settings
      setIsPercentage(data.isPercentage);
      setPercentageValue(data.percentageValue);
      setFixedAmount(data.fixedAmount);
      setIsTaxable(data.isTaxable ?? false); // Default to false if undefined
      
      // UI settings
      setHeadingFontSize(data.uiSettings.headingFontSize);
      setTextFontSize(data.uiSettings.textFontSize);
      setPrimaryColor(data.uiSettings.primaryColor);
      setAccentColor(data.uiSettings.accentColor);
      setCustomText(data.uiSettings.customText);
      
      // Vessel rates
      setVesselRates(data.vesselSpecificRates || []);
    } catch (err) {
      console.error('Error fetching damage waiver settings:', err);
      setError('Failed to load damage waiver settings');
    } finally {
      setLoading(false);
    }
  };

  // Fetch vessel types and vessels
  const fetchVessels = async () => {
    try {
      const vesselsData = await getVesselsByType();
      setVessels(vesselsData);
      setVesselTypes(Object.keys(vesselsData));
    } catch (err) {
      console.error('Error fetching vessels:', err);
    }
  };

  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Save settings
  const handleSaveSettings = async () => {
    if (!settings) return;
    
    try {
      setSaving(true);
      setError(null);
      
      const updatedSettings: Partial<DamageWaiverSettings> = {
        isPercentage,
        percentageValue,
        fixedAmount,
        isTaxable: isTaxable ?? false, // Ensure never undefined
        uiSettings: {
          headingFontSize,
          textFontSize,
          primaryColor,
          accentColor,
          customText
        }
      };
      
      await updateDamageWaiverSettings(updatedSettings);
      setSuccess('Damage waiver settings saved successfully');
      
      // Refresh settings
      fetchSettings();
    } catch (err) {
      console.error('Error saving damage waiver settings:', err);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Open add/edit dialog
  const handleOpenDialog = (rate?: VesselWaiverRate) => {
    if (rate) {
      // Editing existing rate
      setEditingRate(rate);
      setRateType(rate.vesselType ? 'type' : 'vessel');
      setSelectedVesselType(rate.vesselType || '');
      setSelectedVessel(rate.vesselId || '');
      setRateIsPercentage(rate.isPercentage);
      setRatePercentage(rate.percentageValue);
      setRateFixed(rate.fixedAmount);
    } else {
      // Adding new rate
      setEditingRate(null);
      setRateType('type');
      setSelectedVesselType(vesselTypes[0] || '');
      setSelectedVessel('');
      setRateIsPercentage(false);
      setRatePercentage(10);
      setRateFixed(25);
    }
    
    setDialogOpen(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // Save vessel rate
  const handleSaveRate = async () => {
    try {
      setSaving(true);
      
      const rateData: Omit<VesselWaiverRate, 'id'> = {
        isPercentage: rateIsPercentage,
        percentageValue: ratePercentage,
        fixedAmount: rateFixed
      };
      
      if (rateType === 'type') {
        rateData.vesselType = selectedVesselType;
        
        // Check if we already have a rate for this vessel type
        const existingTypeRate = vesselRates.find(r => 
          r.vesselType === selectedVesselType && 
          (!editingRate || r.id !== editingRate.id)
        );
        
        if (existingTypeRate) {
          setError(`A rate for ${selectedVesselType} already exists`);
          setSaving(false);
          return;
        }
      } else {
        const selectedVesselObj = vessels[selectedVesselType]?.find(v => v.id === selectedVessel);
        
        rateData.vesselId = selectedVessel;
        rateData.vesselName = selectedVesselObj?.name || 'Unknown Vessel';
        
        // Check if we already have a rate for this vessel
        const existingVesselRate = vesselRates.find(r => 
          r.vesselId === selectedVessel && 
          (!editingRate || r.id !== editingRate.id)
        );
        
        if (existingVesselRate) {
          setError(`A rate for this vessel already exists`);
          setSaving(false);
          return;
        }
      }
      
      if (editingRate) {
        // Update existing rate
        await updateVesselWaiverRate({
          ...rateData,
          id: editingRate.id
        });
      } else {
        // Add new rate
        await addVesselWaiverRate(rateData);
      }
      
      setSuccess(`Rate ${editingRate ? 'updated' : 'added'} successfully`);
      fetchSettings(); // Refresh settings
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving vessel rate:', err);
      setError('Failed to save rate');
    } finally {
      setSaving(false);
    }
  };

  // Delete vessel rate
  const handleDeleteRate = async (rateId: string) => {
    try {
      setLoading(true);
      await removeVesselWaiverRate(rateId);
      setSuccess('Rate removed successfully');
      fetchSettings(); // Refresh settings
    } catch (err) {
      console.error('Error removing vessel rate:', err);
      setError('Failed to remove rate');
    } finally {
      setLoading(false);
    }
  };

  // Preview panel for damage waiver UI
  const renderPreview = () => {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Preview
        </Typography>
        <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontSize: headingFontSize, 
              fontWeight: 'bold', 
              color: primaryColor 
            }}
          >
            Important: Damage Waiver Information
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              mt: 1, 
              fontSize: textFontSize 
            }}
          >
            Our vessels require significant repair costs if damaged:
          </Typography>
          
          <Grid container spacing={1} sx={{ mt: 0.5, mb: 1 }}>
            <Grid item xs={4}>
              <Typography variant="body2" sx={{ fontSize: textFontSize }}>
                • General repairs: $1,000-$5,000
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" sx={{ fontSize: textFontSize }}>
                • Motor replacement: $10,000+
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" sx={{ fontSize: textFontSize }}>
                • Vessel replacement: $50,000+
              </Typography>
            </Grid>
          </Grid>
          
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 'bold', 
              mt: 1, 
              fontSize: textFontSize 
            }}
          >
            {customText || 'By accepting our damage waiver, you limit your financial responsibility for accidental damage.'}
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              mt: 1, 
              fontSize: textFontSize 
            }}
          >
            If declined, you'll be 100% responsible for ANY damage during your rental, up to full replacement value.
          </Typography>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: textFontSize,
              fontStyle: 'italic',
              color: 'text.secondary'
            }}
          >
            Tax Information: This damage waiver {isTaxable ? 'IS' : 'IS NOT'} subject to sales tax
          </Typography>
        </Paper>
      </Box>
    );
  };

  if (roleLoading) {
    return (
      <MarinaAdminLayout>
        <Box>
          <CircularProgress />
        </Box>
      </MarinaAdminLayout>
    );
  }

  if (!userProfile || roleName !== 'admin') {
    return (
      <MarinaAdminLayout>
        <Box>
          <Alert severity="error" sx={{ mt: 2 }}>
            You don't have permission to access this page
          </Alert>
        </Box>
      </MarinaAdminLayout>
    );
  }

  return (
    <MarinaAdminLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Damage Waiver Settings
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Paper sx={{ mb: 3 }}>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab label="General Settings" />
                <Tab label="Vessel Specific Rates" />
                <Tab label="Appearance" />
              </Tabs>
              
              <TabPanel value={tabValue} index={0}>
                <Typography variant="h6" gutterBottom>
                  Default Damage Waiver Settings
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={isPercentage}
                              onChange={(e) => setIsPercentage(e.target.checked)}
                            />
                          }
                          label={isPercentage ? "Percentage of Rental Price" : "Fixed Amount"}
                        />
                        
                        {isPercentage ? (
                          <TextField
                            fullWidth
                            label="Percentage Value"
                            type="number"
                            value={percentageValue}
                            onChange={(e) => setPercentageValue(Number(e.target.value))}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">%</InputAdornment>,
                              inputProps: { min: 0, max: 100 }
                            }}
                            sx={{ mt: 2 }}
                          />
                        ) : (
                          <TextField
                            fullWidth
                            label="Fixed Amount"
                            type="number"
                            value={fixedAmount}
                            onChange={(e) => setFixedAmount(Number(e.target.value))}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                              inputProps: { min: 0 }
                            }}
                            sx={{ mt: 2 }}
                          />
                        )}
                        
                        <FormControlLabel
                          control={
                            <Switch
                              checked={isTaxable}
                              onChange={(e) => setIsTaxable(e.target.checked)}
                            />
                          }
                          label="Is this waiver subject to sales tax?"
                        />
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                          {isTaxable 
                            ? "✓ Damage waiver fees will be included in taxable amount during checkout"
                            : "○ Damage waiver fees will NOT be taxed (common for insurance products)"
                          }
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                          This is the default damage waiver fee that will be applied to all vessels
                          unless a vessel-specific rate is set.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </TabPanel>
              
              <TabPanel value={tabValue} index={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Vessel Specific Rates
                  </Typography>
                  
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                  >
                    Add Rate
                  </Button>
                </Box>
                
                <Grid container spacing={2}>
                  {vesselRates.length === 0 ? (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">
                          No vessel-specific rates configured. Default rate will be used for all vessels.
                        </Typography>
                      </Paper>
                    </Grid>
                  ) : (
                    vesselRates.map((rate) => (
                      <Grid
                        key={rate.id}
                        item xs={12} md={6}>
                        <Card>
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="h6">
                                {rate.vesselType ? (
                                  <>
                                    <BoatIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                                    All {rate.vesselType}s
                                  </>
                                ) : (
                                  `${rate.vesselName}`
                                )}
                              </Typography>
                              
                              <Box>
                                <IconButton
                                  color="primary"
                                  onClick={() => handleOpenDialog(rate)}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  color="error"
                                  onClick={() => handleDeleteRate(rate.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </Box>
                            
                            <Divider sx={{ my: 1 }} />
                            
                            <Typography variant="body2">
                              <strong>Type:</strong> {rate.isPercentage ? 'Percentage' : 'Fixed Amount'}
                            </Typography>
                            
                            {rate.isPercentage ? (
                              <Typography variant="body2">
                                <strong>Value:</strong> {rate.percentageValue}% of rental price
                              </Typography>
                            ) : (
                              <Typography variant="body2">
                                <strong>Amount:</strong> ${rate.fixedAmount}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))
                  )}
                </Grid>
              </TabPanel>
              
              <TabPanel value={tabValue} index={2}>
                <Typography variant="h6" gutterBottom>
                  Appearance Settings
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom>
                          Text Settings
                        </Typography>
                        
                        <TextField
                          fullWidth
                          label="Heading Font Size"
                          value={headingFontSize}
                          onChange={(e) => setHeadingFontSize(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <FormatSizeIcon />
                              </InputAdornment>
                            )
                          }}
                          sx={{ mb: 2 }}
                        />
                        
                        <TextField
                          fullWidth
                          label="Text Font Size"
                          value={textFontSize}
                          onChange={(e) => setTextFontSize(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <FormatSizeIcon />
                              </InputAdornment>
                            )
                          }}
                          sx={{ mb: 2 }}
                        />
                        
                        <TextField
                          fullWidth
                          label="Custom Text"
                          multiline
                          rows={3}
                          value={customText}
                          onChange={(e) => setCustomText(e.target.value)}
                          placeholder="Enter custom explanation text for the damage waiver"
                          helperText="This text will be displayed in the damage waiver section"
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom>
                          Colors
                        </Typography>
                        
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="body2" gutterBottom>
                            Primary Color
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                              component="input"
                              type="color"
                              value={primaryColor}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPrimaryColor(e.target.value)}
                              sx={{ 
                                width: '100%',
                                height: 40,
                                border: '1px solid #ddd',
                                borderRadius: 1,
                                p: 0
                              }}
                            />
                            <TextField
                              value={primaryColor}
                              onChange={(e) => setPrimaryColor(e.target.value)}
                              size="small"
                              sx={{ ml: 1, width: 120 }}
                            />
                          </Box>
                        </Box>
                        
                        <Box>
                          <Typography variant="body2" gutterBottom>
                            Accent Color
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                              component="input"
                              type="color"
                              value={accentColor}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAccentColor(e.target.value)}
                              sx={{ 
                                width: '100%',
                                height: 40,
                                border: '1px solid #ddd',
                                borderRadius: 1,
                                p: 0
                              }}
                            />
                            <TextField
                              value={accentColor}
                              onChange={(e) => setAccentColor(e.target.value)}
                              size="small"
                              sx={{ ml: 1, width: 120 }}
                            />
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
                
                {renderPreview()}
              </TabPanel>
            </Paper>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSaveSettings}
                disabled={saving}
              >
                {saving ? <CircularProgress size={24} /> : 'Save Settings'}
              </Button>
            </Box>
          </>
        )}
      </Box>
      {/* Add/Edit Rate Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRate ? 'Edit Rate' : 'Add New Rate'}
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Rate Type</InputLabel>
              <Select
                value={rateType}
                label="Rate Type"
                onChange={(e) => setRateType(e.target.value as 'vessel' | 'type')}
              >
                <MenuItem value="type">All Vessels of Type</MenuItem>
                <MenuItem value="vessel">Specific Vessel</MenuItem>
              </Select>
            </FormControl>
            
            {rateType === 'type' ? (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Vessel Type</InputLabel>
                <Select
                  value={selectedVesselType}
                  label="Vessel Type"
                  onChange={(e) => setSelectedVesselType(e.target.value)}
                >
                  {vesselTypes.map(type => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Vessel Type</InputLabel>
                  <Select
                    value={selectedVesselType}
                    label="Vessel Type"
                    onChange={(e: SelectChangeEvent<string>) => {
                      setSelectedVesselType(e.target.value);
                      setSelectedVessel(''); // Reset vessel selection when type changes
                    }}
                  >
                    {vesselTypes.map(type => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Vessel</InputLabel>
                  <Select
                    value={selectedVessel}
                    label="Vessel"
                    onChange={(e) => setSelectedVessel(e.target.value)}
                    disabled={!selectedVesselType}
                  >
                    {selectedVesselType && vessels[selectedVesselType]?.map(vessel => (
                      <MenuItem key={vessel.id} value={vessel.id}>
                        {vessel.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
            
            <Divider sx={{ my: 2 }} />
            
            <FormControlLabel
              control={
                <Switch
                  checked={rateIsPercentage}
                  onChange={(e) => setRateIsPercentage(e.target.checked)}
                />
              }
              label={rateIsPercentage ? "Percentage of Rental Price" : "Fixed Amount"}
            />
            
            {rateIsPercentage ? (
              <TextField
                fullWidth
                label="Percentage Value"
                type="number"
                value={ratePercentage}
                onChange={(e) => setRatePercentage(Number(e.target.value))}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  inputProps: { min: 0, max: 100 }
                }}
                sx={{ mt: 2 }}
              />
            ) : (
              <TextField
                fullWidth
                label="Fixed Amount"
                type="number"
                value={rateFixed}
                onChange={(e) => setRateFixed(Number(e.target.value))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { min: 0 }
                }}
                sx={{ mt: 2 }}
              />
            )}
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={handleSaveRate}
            disabled={
              (rateType === 'type' && !selectedVesselType) ||
              (rateType === 'vessel' && (!selectedVesselType || !selectedVessel)) ||
              (rateIsPercentage && (ratePercentage <= 0 || ratePercentage > 100)) ||
              (!rateIsPercentage && rateFixed <= 0) ||
              saving
            }
          >
            {saving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </MarinaAdminLayout>
  );
};

export default DamageWaiverPage; 